import { useState, useCallback, useEffect } from 'react';

import { notificationApi } from '../services/notificationApi';
import type { Notification } from '../types/notification.types';
import { parseNotificationResponse } from '../utils/notificationUtils';

export const useNotificationData = (userId: string | null) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [totalNotifications, setTotalNotifications] = useState(0);

  const fetchNotifications = useCallback(
    async (page = 1, refresh = false) => {
      if (loading) return;

      try {
        setLoading(true);
        const responseData = await notificationApi.fetchNotifications(page);

        const parsed = parseNotificationResponse(responseData);

        setTotalPages(parsed.totalPages);
        setTotalNotifications(parsed.totalNotifications);
        setHasMore(page < parsed.totalPages);
        setCurrentPage(page);

        if (refresh || page === 1) {
          setNotifications(parsed.notifications);
        } else {
          setNotifications((prev) => [...prev, ...parsed.notifications]);
        }
      } catch (err) {
        console.error('Fetching notifications failed', err);
      } finally {
        setLoading(false);
      }
    },
    [loading],
  );

  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await notificationApi.fetchUnreadCount();
      setUnreadCount(response?.count || 0);
    } catch (err) {
      console.error('Fetching unread count failed', err);
    }
  }, []);

  const loadMoreNotifications = useCallback(async () => {
    if (loading || !hasMore) return;

    try {
      await fetchNotifications(currentPage + 1);
    } catch (error) {
      console.error('Error loading more notifications:', error);
    }
  }, [loading, hasMore, currentPage, fetchNotifications]);

  const refreshNotifications = useCallback(async () => {
    if (loading) {
      return;
    }

    setCurrentPage(1);
    await fetchNotifications(1, true);
    await fetchUnreadCount();
  }, [loading, fetchNotifications, fetchUnreadCount]);

  const getUnreadNotifications = useCallback(() => {
    return notifications.filter((notification) => !notification.is_read);
  }, [notifications]);

  // Initial fetch on mount
  useEffect(() => {
    if (userId) {
      fetchNotifications(1);
      fetchUnreadCount();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  return {
    notifications,
    setNotifications,
    unreadCount,
    setUnreadCount,
    loading,
    currentPage,
    hasMore,
    totalPages,
    totalNotifications,
    setTotalNotifications,
    loadMoreNotifications,
    getUnreadNotifications,
    refreshNotifications,
    fetchNotifications,
    fetchUnreadCount,
  };
};
